import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Users, Award, Filter, RefreshCw, AlertCircle,
  TrendingUp, TrendingDown, Percent, Clock, MapPin
} from 'lucide-react';
import { useCompany } from '../../contexts/CompanyContext';
import { certificates } from '../../lib/api';
import { cn } from '../../utils/cn';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { formatRelativeTime } from '../../utils/date';
import { parseISO, isValid, format, subDays, startOfDay, endOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface DashboardStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  averageScore: number;
  approvalRate: number;
  monthlyTrends: {
    month: string;
    approved: number;
    rejected: number;
  }[];
  dailyCompletions: {
    date: string;
    count: number;
    total: number;
  }[];
  cityDistribution: {
    city: string;
    count: number;
  }[];
  last7DaysTotal: number;
}

export function DashboardPage() {
  const { user } = useAuth();
  const { company } = useCompany();
  const [stats, setStats] = useState<DashboardStats>({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    averageScore: 0,
    approvalRate: 0,
    monthlyTrends: [],
    dailyCompletions: [],
    cityDistribution: [],
    last7DaysTotal: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cityFilter, setCityFilter] = useState<string>('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [cities, setCities] = useState<{id: string, name: string}[]>([]);

  useEffect(() => {
    // Load cities from localStorage
    const storedCities = localStorage.getItem('cities');
    if (storedCities) {
      setCities(JSON.parse(storedCities));
    } else {
      // Default cities if none stored
      const defaultCities = [
        { id: 'anapolisgo', name: 'Anápolis' },
        { id: 'rioverdego', name: 'Rio Verde' },
        { id: 'itumbiara', name: 'Itumbiara' }
      ];
      setCities(defaultCities);
      localStorage.setItem('cities', JSON.stringify(defaultCities));
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [cityFilter]);

  useEffect(() => {
    if (!autoRefresh) return;

    const intervalId = setInterval(() => {
      loadData();
    }, 30000); // 30 seconds

    return () => clearInterval(intervalId);
  }, [autoRefresh, cityFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const certificatesData = await certificates.list();
      
      // Filter by city if selected
      const filteredData = cityFilter
        ? certificatesData.filter(c => c.driver?.city === cityFilter)
        : certificatesData;

      // Group by driver to avoid duplicates
      const uniqueDrivers = filteredData.reduce((acc: any, curr) => {
        if (!curr.driver?.cpf) return acc;

        // Parse and validate the date safely
        const completedAt = curr.completed_at || curr.completedAt;
        const currDate = completedAt ? parseISO(completedAt) : null;

        if (!currDate || !isValid(currDate)) {
          console.warn('Invalid or missing date:', { completedAt, curr });
          return acc;
        }

        const existingEntry = acc[curr.driver.cpf];
        const existingDate = existingEntry?.completed_at 
          ? parseISO(existingEntry.completed_at)
          : null;

        if (!existingEntry || 
            (existingDate && isValid(existingDate) && currDate > existingDate)) {
          acc[curr.driver.cpf] = curr;
        }
        return acc;
      }, {});

      const uniqueCertificates = Object.values(uniqueDrivers);

      // Calculate basic stats
      const total = uniqueCertificates.length;
      const pending = uniqueCertificates.filter(c => c.status === 'pending').length;
      const approved = uniqueCertificates.filter(c => c.status === 'approved').length;
      const rejected = uniqueCertificates.filter(c => c.status === 'rejected').length;
      const averageScore = uniqueCertificates.reduce((acc: number, curr: any) => acc + curr.score, 0) / total || 0;
      const approvalRate = approved > 0 ? (approved / (approved + rejected)) * 100 : 0;

      // Calculate monthly trends
      const last6Months = Array.from({ length: 6 }, (_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        return format(date, 'MMM/yy', { locale: ptBR });
      }).reverse();

      const monthlyTrends = last6Months.map(month => {
        const monthCertificates = uniqueCertificates.filter((c: any) => {
          const completedAt = c.completed_at || c.completedAt;
          if (!completedAt) return false;

          const certDate = parseISO(completedAt);
          if (!isValid(certDate)) return false;

          return format(certDate, 'MMM/yy', { locale: ptBR }) === month;
        });

        return {
          month,
          approved: monthCertificates.filter((c: any) => c.status === 'approved').length,
          rejected: monthCertificates.filter((c: any) => c.status === 'rejected').length
        };
      });

      // Calculate daily completions for last 7 days
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = subDays(new Date(), i);
        return {
          date: format(date, 'dd/MM', { locale: ptBR }),
          fullDate: date
        };
      }).reverse();

      const dailyCompletions = last7Days.map(({ date, fullDate }) => {
        const dayStart = startOfDay(fullDate);
        const dayEnd = endOfDay(fullDate);
        
        const dayCertificates = uniqueCertificates.filter((c: any) => {
          const completedAt = c.completed_at || c.completedAt;
          if (!completedAt) return false;

          const certDate = parseISO(completedAt);
          return isValid(certDate) && certDate >= dayStart && certDate <= dayEnd;
        });

        // Calculate running total
        const previousDays = uniqueCertificates.filter((c: any) => {
          const completedAt = c.completed_at || c.completedAt;
          if (!completedAt) return false;

          const certDate = parseISO(completedAt);
          return isValid(certDate) && certDate <= dayEnd;
        });

        return {
          date,
          count: dayCertificates.length,
          total: previousDays.length
        };
      });

      // Calculate city distribution
      const cityDistribution = cities.map(city => ({
        city: city.name,
        count: uniqueCertificates.filter((c: any) => c.driver?.city === city.name).length
      }));

      // Calculate last 7 days total
      const last7DaysTotal = dailyCompletions.reduce((acc, curr) => acc + curr.count, 0);

      setStats({
        total,
        pending,
        approved,
        rejected,
        averageScore,
        approvalRate,
        monthlyTrends,
        dailyCompletions,
        cityDistribution,
        last7DaysTotal
      });

      setLastRefresh(new Date());
    } catch (err: any) {
      console.error('Error loading dashboard data:', err);
      setError(err.message || 'Erro ao carregar dados. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const statusChartData = {
    labels: ['Aprovados', 'Pendentes', 'Reprovados'],
    datasets: [
      {
        data: [stats.approved, stats.pending, stats.rejected],
        backgroundColor: ['#22C55E', '#EAB308', '#EF4444'],
        borderWidth: 0
      }
    ]
  };

  const trendsChartData = {
    labels: stats.monthlyTrends.map(t => t.month),
    datasets: [
      {
        label: 'Aprovados',
        data: stats.monthlyTrends.map(t => t.approved),
        backgroundColor: '#22C55E',
        borderColor: '#22C55E',
        borderWidth: 1
      },
      {
        label: 'Reprovados',
        data: stats.monthlyTrends.map(t => t.rejected),
        backgroundColor: '#EF4444',
        borderColor: '#EF4444',
        borderWidth: 1
      }
    ]
  };

  const dailyCompletionsData = {
    labels: stats.dailyCompletions.map(d => d.date),
    datasets: [
      {
        label: 'Novos Treinamentos',
        data: stats.dailyCompletions.map(d => d.count),
        backgroundColor: '#7C3AED',
        borderColor: '#7C3AED',
        borderWidth: 1
      }
    ]
  };

  const cityDistributionData = {
    labels: stats.cityDistribution.map(d => d.city),
    datasets: [
      {
        label: 'Condutores por Cidade',
        data: stats.cityDistribution.map(d => d.count),
        backgroundColor: ['#3B82F6', '#8B5CF6'],
        borderColor: ['#3B82F6', '#8B5CF6'],
        borderWidth: 1
      }
    ]
  };

  const trendsChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  const dailyCompletionsOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Treinamentos Realizados por Dia',
        font: {
          size: 16,
          weight: 'bold' as const
        }
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const dataIndex = context.dataIndex;
            const total = stats.dailyCompletions[dataIndex].total;
            return [
              `Novos Treinamentos: ${context.raw}`,
              `Total Acumulado: ${total}`
            ];
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  const cityDistributionOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: 'Distribuição por Cidade',
        font: {
          size: 16,
          weight: 'bold' as const
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Visão Geral
          </h1>
          <p className="mt-1 text-gray-500">
            Dashboard com estatísticas e informações gerais
            <span className="ml-2 text-xs text-gray-400">
              Última atualização: {formatRelativeTime(lastRefresh)}
            </span>
          </p>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className={cn(
                "block rounded-lg border-gray-300",
                "focus:ring-purple-500 focus:border-purple-500"
              )}
            >
              <option value="">Todas as Cidades</option>
              {cities.map(city => (
                <option key={city.id} value={city.name}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={cn(
              "flex items-center gap-2 rounded-lg px-4 py-2 text-sm",
              "transition-colors border",
              autoRefresh 
                ? "border-purple-200 bg-purple-50 text-purple-700"
                : "border-gray-200 bg-white text-gray-700"
            )}
          >
            <RefreshCw className={cn(
              "h-4 w-4",
              autoRefresh && "animate-spin"
            )} />
            {autoRefresh ? 'Atualização Automática' : 'Atualização Manual'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
        <div className="relative overflow-hidden rounded-lg bg-white p-6 shadow-sm">
          <dt>
            <div className="absolute rounded-lg p-3 bg-purple-100">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <p className="ml-16 truncate text-sm font-medium text-gray-500">
              Total de Certificados
            </p>
          </dt>
          <dd className="ml-16 flex items-baseline">
            <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
          </dd>
        </div>

        <div className="relative overflow-hidden rounded-lg bg-white p-6 shadow-sm">
          <dt>
            <div className="absolute rounded-lg p-3 bg-yellow-100">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
            <p className="ml-16 truncate text-sm font-medium text-gray-500">
              Pendentes
            </p>
          </dt>
          <dd className="ml-16 flex items-baseline">
            <p className="text-2xl font-semibold text-gray-900">{stats.pending}</p>
          </dd>
        </div>

        <div className="relative overflow-hidden rounded-lg bg-white p-6 shadow-sm">
          <dt>
            <div className="absolute rounded-lg p-3 bg-green-100">
              <Award className="h-6 w-6 text-green-600" />
            </div>
            <p className="ml-16 truncate text-sm font-medium text-gray-500">
              Média de Aproveitamento
            </p>
          </dt>
          <dd className="ml-16 flex items-baseline">
            <p className="text-2xl font-semibold text-gray-900">
              {stats.averageScore.toFixed(1)}%
            </p>
          </dd>
        </div>

        <div className="relative overflow-hidden rounded-lg bg-white p-6 shadow-sm">
          <dt>
            <div className="absolute rounded-lg p-3 bg-blue-100">
              <Percent className="h-6 w-6 text-blue-600" />
            </div>
            <p className="ml-16 truncate text-sm font-medium text-gray-500">
              Taxa de Aprovação
            </p>
          </dt>
          <dd className="ml-16 flex items-baseline">
            <p className="text-2xl font-semibold text-gray-900">
              {stats.approvalRate.toFixed(1)}%
            </p>
            <div className="ml-2">
              {stats.approvalRate >= 70 ? (
                <TrendingUp className="h-4 w-4 text-green-500" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-500" />
              )}
            </div>
          </dd>
        </div>

        <div className="relative overflow-hidden rounded-lg bg-white p-6 shadow-sm">
          <dt>
            <div className="absolute rounded-lg p-3 bg-indigo-100">
              <MapPin className="h-6 w-6 text-indigo-600" />
            </div>
            <p className="ml-16 truncate text-sm font-medium text-gray-500">
              Últimos 7 Dias
            </p>
          </dt>
          <dd className="ml-16 flex items-baseline">
            <p className="text-2xl font-semibold text-gray-900">
              {stats.last7DaysTotal}
            </p>
          </dd>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Status Distribution */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-6">
            Distribuição de Status
          </h3>
          <div className="h-64">
            <Doughnut 
              data={statusChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom'
                  }
                }
              }}
            />
          </div>
        </div>

        {/* City Distribution */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="h-64">
            <Bar 
              data={cityDistributionData}
              options={cityDistributionOptions}
            />
          </div>
        </div>

        {/* Monthly Trends */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-6">
            Tendências Mensais
          </h3>
          <div className="h-64">
            <Bar 
              data={trendsChartData}
              options={trendsChartOptions}
            />
          </div>
        </div>

        {/* Daily Completions */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="h-64">
            <Bar 
              data={dailyCompletionsData}
              options={dailyCompletionsOptions}
            />
          </div>
        </div>
      </div>
    </div>
  );
}